import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal } from '@mui/material';
import { formCounterSlice } from '../../../redux/formCounter';
import { ProfileEditContext } from 'views/Dashboards-B2B/components/BACKOFFICEFLOW/CLIENTVIEW/components/ClientProfile/components/ProfileEditContext';
import {
    Actions as ModalActions,
    PrimaryButton as ModalPrimaryButton,
    SecondaryButton as ModalSecondaryButton,
} from 'components/common/Modal';
import { authUser } from 'redux/auth/auth-middleware';
import ConfirmationModal from 'components/forms/ConfirmationModal/ConfirmationModal';
import { useAuth } from '../../../contexts/ProtectedRoute';
import api, { LoginPayload, RegisterPayload } from 'api';
import storage from 'utils/storageController';
import { ReactComponent as CrossSimpleIcon } from 'assets/icons/cross-simple.svg';
import IconBtn from '../IconBtn';
import styles from '../../forms/ConfirmationModal/ConfirmationModal.module.scss';
import './formBox.css';
import { deviceType, osName } from 'react-device-detect';
import { getLocation } from '../../../utils/location';
import { getCookie } from '../../../utils/cookies';
import { BackendEventProps, FrontendEventProps } from 'api/models/payload/EventsPayload';
import ROLE from './../../../assets/userRoles.json';
import * as S from './FormBox.styled';
import { useWindowSize } from 'hooks/useWindowSize';
import useEnabledCountries from 'hooks/useEnabledCountries';
import { useInflight } from 'hooks/useInflight';
import DOMAIN from '../../../assets/domains.json';
import { DomainKey } from 'data/constants/domainApiKey';
import { useTranslation } from 'react-i18next';
import { toPurposePayload } from 'helpers/purpose';
import { stampCompletedAssessmentIfReady } from 'utils/completedAssessment';
import { ACCOUNT_HOME, selfServeEntryPath } from 'utils/countries';
import { labelToKey } from 'utils/formLabelsToKey';
import { omitHasSpouse } from 'utils/hasSpouse';
import { getActingProfileUserId, isActingAsSpouseProfile } from 'utils/selectedAccount';
import { useFormPath } from 'utils/useFormPath';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { Permission } from 'api/models/response/permissions';
import PERMISSION from './../../../assets/permissions.json';
import { isSalesPersonRole } from 'data/constants/roles';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { setAlert } from 'redux/alert';

interface Props {
    validation?: CallableFunction;
    tableNames: string[];
    children: React.ReactNode;
    data: object[];
    onBeforeSave?: () => boolean | Promise<boolean>;
    onAfterSave?: (results: Array<{ tableName: string; data: unknown }>) => void;
    /** Write these tables to this user (spouse User id). Same payloads as the candidate editor. */
    targetUserId?: number | null;
    /** Never POST has_spouse onto a spouse user. */
    omitHasSpouse?: boolean;
}

const WORK_EXPERIENCE_TABLE = 'WorkExperienceNew';
const WORK_EXPERIENCE_ACK_KEY = 'workExperienceNewAcknowledged';
const USER_TABLE = 'User';

const tablePayload = (tableName: string, payload: object, includeHasSpouse: boolean) => {
    if (tableName === 'AdditionalDetails' && !includeHasSpouse) {
        return omitHasSpouse(payload);
    }
    if (tableName === 'Purpose') {
        return toPurposePayload(payload);
    }
    return payload;
};

const hasAcknowledgedWorkExperience = (): boolean => {
    try {
        return sessionStorage.getItem(WORK_EXPERIENCE_ACK_KEY) === '1';
    } catch {
        return false;
    }
};

const markWorkExperienceAcknowledged = (): void => {
    try {
        sessionStorage.setItem(WORK_EXPERIENCE_ACK_KEY, '1');
    } catch {
        // sessionStorage may be unavailable; ignore and fall back to in-memory flag
    }
};

const country: string = getLocation().userCountry;
const sessionId = getCookie('JSESSIONID');

const FormBox = (props: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const params = useParams();
    const { screen, no_screens, purpose, canEnableSubmitButton } = useSelector(
        (state: RootState) => state.formCounter
    );
    const user = useSelector((state: RootState) => state.user.entity);

    // FormBox is rendered in two contexts:
    //   (1) sales / backoffice flow — route has :id (the client being edited)
    //   (2) client flow — no :id in the route; the authenticated user is
    //       filling their own forms, so fall back to their own id.
    // Without the fallback Number(params.id) is NaN and PATCH requests hit
    // /candidate_info/NaN/completed-forms → backend 500.
    const routeId = Number(params.id);
    const candidate_id = Number.isFinite(routeId) && routeId > 0 ? routeId : user.id;
    const { next, prev } = formCounterSlice.actions;
    const { applications: candidateApps } = useSelector((state: RootState) => state.applications);
    const { permissionsList } = useSelector((state: RootState) => state.permissions);

    const [open, setOpen] = useState<boolean>(false);
    const [workExpConfirmOpen, setWorkExpConfirmOpen] = useState(false);
    // Sync flag so confirm → re-run does not re-open the modal (setState is async).
    // sessionStorage keeps it one-time for the browser session.
    const workExpAcknowledgedRef = useRef(hasAcknowledgedWorkExperience());

    const dispatch = useDispatch<AppDispatch>();
    const [queryParameters] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { role } = useAuth();
    const { codes } = useEnabledCountries();
    const profileEdit = useContext(ProfileEditContext);
    const [profileSaveState, setProfileSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');

    const isUnqualified = role === ROLE.unqualified;
    const isSalesPerson = isSalesPersonRole(role);
    const explicitTargetId =
        typeof props.targetUserId === 'number' &&
        Number.isFinite(props.targetUserId) &&
        props.targetUserId > 0
            ? props.targetUserId
            : undefined;
    const actingProfileId = getActingProfileUserId();
    const writeUserId = explicitTargetId ?? actingProfileId ?? candidate_id;
    const writeByUserId =
        explicitTargetId !== undefined ||
        actingProfileId !== undefined ||
        isSalesPerson ||
        (Number.isFinite(routeId) && routeId > 0);
    // Table data may go to the spouse User; the assessment owner's completed-forms
    // row still owns the `spouse` step (and other wizard steps).
    const completionUserId = actingProfileId ?? candidate_id;

    const writeTable = (payload: object, tableName: string) => {
        if (writeByUserId && Number.isFinite(writeUserId) && writeUserId > 0) {
            return api.tableInput.postSales(payload, tableName, writeUserId);
        }
        return api.tableInput.post(payload, tableName);
    };

    const includeHasSpouseOnSave = !props.omitHasSpouse && !isActingAsSpouseProfile();
    const isVisaCandidate = role === ROLE.completedAssessment;
    const isExternalClient = role === ROLE.externalClient;
    const buttonText =
        screen >= no_screens
            ? t('submit', { lng: language })
            : t('save&Proceed', { lng: language });
    const formPath = useFormPath(purpose, candidate_id);
    const [currFormName, setCurrFormName] = useState('');
    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        setCurrFormName(formPath[screen - 1] ?? '');
    }, [formPath, purpose, screen]);

    const handleRegister = async (registerPayload: RegisterPayload) => {
        let url_params: URLSearchParams | null = null;
        if (window.document.referrer) {
            url_params = new URL(window.document.referrer).searchParams;
        }
        const event_properties: BackendEventProps = {
            country: country,
            referrer: window.document.referrer,
            device_type: deviceType,
            utm_source: queryParameters.get('utm_source') || url_params?.get('utm_source') || '',
            utm_medium: queryParameters.get('utm_medium') || url_params?.get('utm_medium') || '',
            utm_campaign:
                queryParameters.get('utm_campaign') || url_params?.get('utm_campaign') || '',
            utm_content: queryParameters.get('utm_content') || url_params?.get('utm_content') || '',
            user_agent: window.navigator.userAgent,
            anonymous_id: localStorage.ajs_anonymous_id?.replaceAll('"', ''),
        };
        const _registerPayload = { ...registerPayload, ...event_properties };
        const res = await api.register.register(_registerPayload);
        const response = res.data;
        return response;
    };

    const trackEventsHandle = (origin: string, action: 'next' | 'back') => {
        const url = location.pathname + `/${currFormName?.toLowerCase().replace(' ', '_')}`;
        if (storage.isAuthenticated()) {
            const url_params = window.document.referrer
                ? new URL(window.document.referrer).searchParams
                : null;
            const _eventParams: FrontendEventProps = {
                page_title: url,
                page_url: url,
                os_type: osName,
                country: country,
                user_id: `${user.id}`,
                session_id: sessionId,
                device_type: deviceType,
                referrer: window.document.referrer,
                user_agent: window.navigator.userAgent,
                utm_source:
                    queryParameters.get('utm_source') || url_params?.get('utm_source') || '',
                utm_medium:
                    queryParameters.get('utm_medium') || url_params?.get('utm_medium') || '',
                utm_campaign:
                    queryParameters.get('utm_campaign') || url_params?.get('utm_campaign') || '',
                utm_content:
                    queryParameters.get('utm_content') || url_params?.get('utm_content') || '',
                anonymousId: localStorage.ajs_anonymous_id?.replaceAll('"', ''),
            };
            if (DomainKey === DOMAIN.ALIRO) {
                (async () => {
                    const eventName = 'form_navigation';
                    const res = await api.events.frontend_event_post(eventName, _eventParams);
                    if (res.status !== 200 && res.status !== 201) {
                        console.log(res.statusText);
                    }
                })();
            }
        }
    };

    const handleLeftClick = () => {
        const origin = props.tableNames[0];
        trackEventsHandle(origin, 'back');
        for (let i = 0; i < props.tableNames.length; i++) {
            const tableName = props.tableNames[i];
            // New users are created via /register, not candidate_info/User.
            if (tableName === USER_TABLE && !storage.isAuthenticated()) {
                continue;
            }
            writeTable(tablePayload(tableName, props.data[i], false), tableName);
        }
        dispatch(prev());
    };

    // todo: refactor this move in different util: 100%, this is a mess :)
    const handleRightClickImpl = async () => {
        const origin = props.tableNames[0];

        trackEventsHandle(origin, 'next');

        if (!props.validation || (await props.validation())) {
            if (props.onBeforeSave && !(await props.onBeforeSave())) {
                return;
            }
            // One-time reminder before saving work experience
            if (
                props.tableNames.includes(WORK_EXPERIENCE_TABLE) &&
                !workExpAcknowledgedRef.current
            ) {
                setWorkExpConfirmOpen(true);
                return;
            }

            const results: Array<{ tableName: string; data: unknown }> = [];
            let shouldMarkFormCompleted = false;
            try {
                for (let i = 0; i < props.tableNames.length; i++) {
                    const tableName = props.tableNames[i];
                    if (tableName === USER_TABLE && !storage.isAuthenticated()) {
                        const registerPayload = props.data[i] as RegisterPayload;
                        const response = await handleRegister(registerPayload);
                        storage.setRefreshToken(response.refresh_token);
                        const formData: LoginPayload = {
                            email: registerPayload.email,
                            password: registerPayload.password,
                        };
                        await dispatch(authUser(formData));
                        continue;
                    }
                    const resp = await writeTable(
                        tablePayload(tableName, props.data[i], includeHasSpouseOnSave),
                        tableName
                    );
                    results.push({ tableName, data: resp?.data });
                    shouldMarkFormCompleted = true;
                }
                props.onAfterSave?.(results);
                if (shouldMarkFormCompleted) {
                    await formCompletedStatusChange();
                }
                const patchedKey = labelToKey[currFormName];
                await stampCompletedAssessmentIfReady({
                    userId: candidate_id,
                    completedAssessment:
                        profileEdit?.completedAssessment ??
                        (isSalesPerson ? null : user.completed_assessment),
                    patched: patchedKey ? { [patchedKey]: true } : undefined,
                    requiredKeys: profileEdit?.requiredFormKeys,
                });
            } catch (err) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(
                            err,
                            t('saveFailed', {
                                lng: language,
                                defaultValue: 'Could not save. Please try again.',
                            })
                        ),
                        type: 'error',
                    })
                );
                return;
            }

            if (screen >= no_screens) {
                if (isUnqualified) {
                    navigate(selfServeEntryPath(codes, 'unqualified') ?? ACCOUNT_HOME);
                } else if (isSalesPerson) {
                    navigate('results');
                    submitAssessmentStatusAdmin(user?.id);
                } else if (isVisaCandidate) {
                    navigate(selfServeEntryPath(codes) ?? ACCOUNT_HOME, {
                        state: { source: 'confirmationModal' },
                    });
                } else if (isExternalClient) {
                    //set assessment as submitted
                    submitAssessmentStatus(user?.id);
                } else {
                    handleOpen();
                }
            } else {
                dispatch(next());
            }
        }
    };

    // In-flight gate around handleRightClick. The register-then-auto-login
    // branch above fires POST /register followed by POST /login; without
    // this gate a double-click on "Save & Proceed" would register the user
    // twice and log them in twice. The gate is broader than that branch on
    // purpose: the per-table inputs also shouldn't double-POST.
    const { pending: submittingForm, run: handleRightClick } = useInflight(async () => {
        await handleRightClickImpl();
    });

    const handleWorkExpConfirm = () => {
        workExpAcknowledgedRef.current = true;
        markWorkExperienceAcknowledged();
        setWorkExpConfirmOpen(false);
        // Re-run save/proceed through the same in-flight gate
        void handleRightClick();
    };

    const handleWorkExpCancel = () => {
        setWorkExpConfirmOpen(false);
    };

    const handleExternalRedirection = (url: string) => {
        window.location.assign(url);
    };

    const handleRedirect = () => {
        //first available active case
        if (permissionsList.some((permission: Permission) => permission.name === PERMISSION.VIEW)) {
            navigate(`/visa-process/${candidateApps[0]?.id}/results`);
        } else {
            navigate(`/visa-process/${candidateApps[0]?.id}/agreement`);
        }
    };

    const formCompletedStatusChange = async () => {
        if (!Number.isFinite(completionUserId) || completionUserId <= 0) return;
        const formKey = labelToKey[currFormName];
        if (!formKey) return;
        try {
            await api.completedForms.editCompletedFormStatus(completionUserId, {
                [formKey]: true,
            });
        } catch (err) {
            console.log(err);
        }
    };

    // Profile-edit mode save (triggered by the ClientProfile edit pane via
    // ProfileEditContext). Posts table inputs for each entry in `tableNames`
    // exactly like `handleRightClick` does, then PATCHes completed-forms with
    // the known form key. Does not navigate or advance `screen`. When every
    // completed-forms field is true and the candidate's completed_assessment
    // is still unset, stamps it via POST .../user/completed-assessment.
    const handleProfileEditSave = async () => {
        if (!profileEdit) return;
        if (props.validation && !(await props.validation())) return;
        if (props.onBeforeSave && !(await props.onBeforeSave())) return;
        setProfileSaveState('saving');
        try {
            const results: Array<{ tableName: string; data: unknown }> = [];
            for (let i = 0; i < props.tableNames.length; i++) {
                const tableName = props.tableNames[i];
                const resp = await writeTable(
                    tablePayload(tableName, props.data[i], includeHasSpouseOnSave),
                    tableName
                );
                results.push({ tableName, data: resp?.data });
            }
            props.onAfterSave?.(results);
            let patchedKey: string | undefined;
            if (profileEdit.formKey && Number.isFinite(completionUserId) && completionUserId > 0) {
                try {
                    await api.completedForms.editCompletedFormStatus(completionUserId, {
                        [profileEdit.formKey]: true,
                    });
                    patchedKey = profileEdit.formKey;
                } catch (err) {
                    console.log(err);
                }
            }
            await stampCompletedAssessmentIfReady({
                userId: completionUserId,
                completedAssessment: profileEdit.completedAssessment,
                patched: patchedKey ? { [patchedKey]: true } : undefined,
                requiredKeys: profileEdit.requiredFormKeys,
            });
            setProfileSaveState('saved');
            profileEdit.onSaved?.();
            setTimeout(() => setProfileSaveState('idle'), 1500);
        } catch (err) {
            console.error('Profile edit save failed', err);
            dispatch(
                setAlert({
                    message: getErrorMessage(
                        err,
                        t('saveFailed', {
                            lng: language,
                            defaultValue: 'Could not save. Please try again.',
                        })
                    ),
                    type: 'error',
                })
            );
            setProfileSaveState('idle');
        }
    };

    // When a sales agent fills a form on behalf of a candidate, the assessment
    // status must be saved against the candidate's id (from the URL), not the
    // logged-in agent's own user id.
    const resolveAssessmentTargetId = (userId: string | number) =>
        isSalesPerson && Number.isFinite(candidate_id) && candidate_id > 0 ? candidate_id : userId;

    const submitAssessmentStatus = async (userId: string | number) => {
        try {
            const targetId = resolveAssessmentTargetId(userId);
            const resp = await api.tableInput.saveAssessmentStatus(targetId);
            if (resp?.data.url) {
                handleExternalRedirection(resp?.data.url);
            } else {
                handleRedirect();
            }
        } catch (err) {
            console.log(err);
        }
    };

    const submitAssessmentStatusAdmin = async (userId: string | number) => {
        try {
            const targetId = resolveAssessmentTargetId(userId);
            await api.tableInput.saveAssessmentStatus(targetId);
        } catch (err) {
            console.log(err);
        }
    };

    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);
    useEffect(() => {
        if (width > 768) {
            setMobile(false);
        }
        if (width <= 768) {
            setMobile(true);
        }
    }, [width, height]);

    if (profileEdit?.active) {
        const savedLabel = t('saved', { lng: language, defaultValue: 'Saved' });
        const savingLabel = t('saving', { lng: language, defaultValue: 'Saving…' });
        const saveLabel =
            profileEdit.saveLabel ??
            t('saveSection', { lng: language, defaultValue: 'Save section' });
        const cancelLabel =
            profileEdit.cancelLabel ?? t('cancel', { lng: language, defaultValue: 'Cancel' });
        const primaryLabel =
            profileSaveState === 'saving'
                ? savingLabel
                : profileSaveState === 'saved'
                  ? savedLabel
                  : saveLabel;
        return (
            <div className={!isSalesPerson ? 'form-container' : 'sales-form-container'}>
                <div className="form-card">{props.children}</div>
                {/* The className is the hook EditPane uses to dock this row to
                 * the bottom of the visible edit pane (position: sticky), so
                 * Cancel / Save section stay reachable without scrolling. */}
                <ModalActions className="profile-edit-actions">
                    <ModalSecondaryButton type="button" onClick={() => profileEdit.onCancel?.()}>
                        {cancelLabel}
                    </ModalSecondaryButton>
                    <ModalPrimaryButton
                        type="button"
                        onClick={handleProfileEditSave}
                        disabled={profileSaveState === 'saving'}
                    >
                        {primaryLabel}
                    </ModalPrimaryButton>
                </ModalActions>
            </div>
        );
    }

    return (
        <div className={!isSalesPerson ? 'form-container' : 'sales-form-container'}>
            <div className="form-card">{props.children}</div>
            <S.Buttons mobile={mobile}>
                {mobile ? (
                    <S.SaveButton onClick={handleLeftClick} mobile={mobile}>
                        {t('back', { lng: language })}
                    </S.SaveButton>
                ) : (
                    <div />
                )}

                <S.SaveButton
                    onClick={() => handleRightClick()}
                    mobile={mobile}
                    aria-busy={submittingForm}
                    disabled={
                        submittingForm ||
                        (!canEnableSubmitButton && screen >= no_screens && !isSalesPerson)
                    }
                >
                    {submittingForm
                        ? t('saving', { lng: language, defaultValue: 'Saving…' })
                        : buttonText}
                </S.SaveButton>
            </S.Buttons>

            <Modal sx={{ zIndex: '10000' }} open={open} onClose={handleClose}>
                <div className="modal-wrapper">
                    <div className={styles.modal}>
                        <ConfirmationModal />
                        <div className={styles['modal__btn-wrapper']}>
                            <IconBtn
                                style={{ height: '24px', width: '24px' }}
                                onClick={handleClose}
                            >
                                <CrossSimpleIcon className={styles['close-icon']} />
                            </IconBtn>
                        </div>
                    </div>
                </div>
            </Modal>

            {workExpConfirmOpen && (
                <ConfirmationDialog
                    title={t('workExperience', { lng: language })}
                    content={t('workExperienceNote', { lng: language })}
                    onClose={handleWorkExpCancel}
                    onConfirm={handleWorkExpConfirm}
                />
            )}
        </div>
    );
};

export default FormBox;
